import {
  Activity,
  Bell,
  Database,
  LayoutDashboard,
  LineChart,
  MessagesSquare,
  Palette,
  ScrollText,
  Settings,
  Trophy,
  Users,
  UsersRound,
  HeartHandshake,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { AdminCapability } from '@/lib/permissions';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /**
   * Capability required to see this section at all. Content managers and
   * moderators must not be shown doors they cannot open — the brief calls for
   * roles that "cannot reach Tier 3 league operations".
   */
  capability: AdminCapability;
  /** Key into the attention-count payload, when this section can be backed up. */
  badgeKey?: 'attention' | 'moderation' | 'failedJobs';
}

export interface NavGroup {
  label?: string;
  items: NavItem[];
}

/**
 * Top-level navigation, per the brief's section 4. Deliberately a sidebar and a
 * mobile slide-over drawer rather than a bottom tab bar — there are too many
 * top-level sections for tabs.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      {
        label: 'Dashboard',
        to: '/',
        icon: LayoutDashboard,
        capability: 'dashboard.view',
        badgeKey: 'attention',
      },
      // Sits beside the ops home but behind its own capability — same glance-level
      // question, different audience. Moderators and content managers do not see it.
      { label: 'Metrics', to: '/metrics', icon: LineChart, capability: 'metrics.view' },
    ],
  },
  {
    label: 'Entities',
    items: [
      { label: 'Leagues', to: '/leagues', icon: Trophy, capability: 'leagues.view' },
      { label: 'Users', to: '/users', icon: Users, capability: 'users.view' },
      { label: 'Squads', to: '/squads', icon: UsersRound, capability: 'squads.view' },
      {
        label: 'Communities',
        to: '/communities',
        icon: MessagesSquare,
        capability: 'communities.view',
        badgeKey: 'moderation',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Content', to: '/content', icon: Palette, capability: 'content.view' },
      {
        label: 'Data Ops',
        to: '/data-ops',
        icon: Database,
        capability: 'dataops.view',
        badgeKey: 'failedJobs',
      },
      {
        label: 'Notifications',
        to: '/notifications',
        icon: Bell,
        capability: 'notifications.view',
      },
    ],
  },
  {
    label: 'Governance',
    items: [
      {
        label: 'Philanthropy',
        to: '/philanthropy',
        icon: HeartHandshake,
        capability: 'philanthropy.view',
      },
      { label: 'Audit', to: '/audit', icon: ScrollText, capability: 'audit.view' },
      { label: 'Settings', to: '/settings', icon: Settings, capability: 'settings.view' },
    ],
  },
  {
    label: 'Reference',
    items: [
      {
        label: 'Design system',
        to: '/design-system',
        icon: Activity,
        capability: 'dashboard.view',
      },
    ],
  },
];
